import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, CircularProgress,
  Alert, Stack, Chip, IconButton, TextField, Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload as UploadIcon,
  Check as AcceptIcon,
  Close as DiscardIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { imagesApi } from '../api/services';
import type { ImageInput, ImageAnalysisResult } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import type { Priority } from '../types/models';

export default function ImagesPage() {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [currentImage, setCurrentImage] = useState<ImageInput | null>(null);
  const [analysisResults, setAnalysisResults] = useState<ImageAnalysisResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setSuccess(null);
    setAnalysisResults([]);

    try {
      const image = await imagesApi.upload(file);
      setCurrentImage(image);

      // Automatically analyse after upload
      setAnalysing(true);
      const results = await imagesApi.analyse(image.id);
      setAnalysisResults(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload/analyse image');
    } finally {
      setUploading(false);
      setAnalysing(false);
    }
  }, []);

  const handleAccept = async () => {
    if (!currentImage || analysisResults.length === 0) return;
    try {
      const workItems = await imagesApi.accept(currentImage.id, analysisResults);
      setSuccess(`Created ${workItems.length} work item(s) from image`);
      setCurrentImage(null);
      setAnalysisResults([]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDiscard = async () => {
    if (!currentImage) return;
    try {
      await imagesApi.discard(currentImage.id);
      setCurrentImage(null);
      setAnalysisResults([]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateResult = (index: number, updates: Partial<ImageAnalysisResult>) => {
    setAnalysisResults(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  const removeResult = (index: number) => {
    setAnalysisResults(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Image Capture</Typography>
        <Typography variant="subtitle1">Upload an image to extract actionable work items using AI</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Upload area */}
      {!currentImage && (
        <Card
          sx={{
            p: 6, textAlign: 'center', border: '2px dashed',
            borderColor: theme.palette.divider, cursor: 'pointer',
            bgcolor: theme.palette.background.paper,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
          component="label"
        >
          <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
          {uploading || analysing ? (
            <Box>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6">{uploading ? 'Uploading...' : 'Analysing with AI...'}</Typography>
              <Typography color="text.secondary">Extracting work items from your image</Typography>
            </Box>
          ) : (
            <Box>
              <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Drop an image or click to upload</Typography>
              <Typography color="text.secondary">
                Screenshots, photos of whiteboards, email captures, error messages, meeting slides
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Supported: JPEG, PNG, GIF, WebP, BMP (max 10 MB)
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {/* Analysis Results — Preview before saving */}
      {analysisResults.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3 }}>
            <Typography variant="h5">
              Extracted Work Items ({analysisResults.length})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<AcceptIcon />} onClick={handleAccept} color="success">
                Accept All
              </Button>
              <Button variant="outlined" startIcon={<DiscardIcon />} onClick={handleDiscard} color="error">
                Discard
              </Button>
            </Stack>
          </Box>

          <Stack spacing={2}>
            {analysisResults.map((result, index) => (
              <Card key={index} sx={{ borderLeft: 3, borderColor: theme.palette.primary.main }}>
                <CardContent>
                  {editIndex === index ? (
                    <Stack spacing={2}>
                      <TextField label="Title" fullWidth value={result.title} onChange={e => updateResult(index, { title: e.target.value })} />
                      <TextField label="Description" fullWidth multiline rows={2} value={result.description || ''} onChange={e => updateResult(index, { description: e.target.value })} />
                      <Stack direction="row" spacing={2}>
                        <TextField label="Type" fullWidth value={result.workItemType} onChange={e => updateResult(index, { workItemType: e.target.value })} />
                        <TextField label="Priority" fullWidth value={result.priority} onChange={e => updateResult(index, { priority: e.target.value })} />
                        <TextField label="Duration (min)" type="number" fullWidth value={result.suggestedDurationMinutes} onChange={e => updateResult(index, { suggestedDurationMinutes: parseInt(e.target.value) || 30 })} />
                      </Stack>
                      <Button onClick={() => setEditIndex(null)}>Done Editing</Button>
                    </Stack>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6">{result.title}</Typography>
                          {result.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{result.description}</Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => setEditIndex(index)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => removeResult(index)}><DiscardIcon fontSize="small" /></IconButton>
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                        <Chip label={result.workItemType} size="small" variant="outlined" />
                        <PriorityChip priority={result.priority as Priority} />
                        <Chip label={`${result.suggestedDurationMinutes}m`} size="small" variant="outlined" />
                        {result.category && <Chip label={result.category} size="small" variant="outlined" />}
                        {result.suggestedStrategicDriver && (
                          <Chip label={`→ ${result.suggestedStrategicDriver}`} size="small" color="primary" variant="outlined" />
                        )}
                        {result.flags.map(f => (
                          <Chip key={f} label={f} size="small" variant="outlined" />
                        ))}
                      </Stack>
                      {result.imageDescription && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          Source: {result.imageDescription}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
